import type {
  TBrand,
  TCreateBrand,
  TCreateEquipment,
  TEquipment,
  TModel,
  TModelCreate
} from '@/AdminPanel/pages/Auto/types/auto.types';
import { ApiConnector } from '@/api/api.connector';

class AutoApi extends ApiConnector {
  getBrands(
    limit?: number,
    page?: number,
    searchTerm?: string
  ): Promise<{
    total: number;
    data: TBrand[];
  }> {
    const limitVal = limit ?? 10;
    const pageVal = page ?? 1;
    const offset = (pageVal - 1) * limitVal;
    return this.call({
      path: 'admin/brands',
      params: {
        limit: limitVal,
        offset,
        searchTerm
      }
    });
  }

  getModels(
    limit?: number,
    page?: number,
    searchTerm?: string,
    brand?: string,
    isCommercial?: boolean
  ): Promise<{
    total: number;
    data: TModel[];
  }> {
    const limitVal = limit ?? 10;
    const pageVal = page ?? 1;
    const offset = (pageVal - 1) * limitVal;
    return this.call({
      path: 'admin/models',
      params: {
        limit: limitVal,
        offset,
        searchTerm,
        brand,
        isCommercial
      }
    });
  }

  getEquipments(
    limit?: number,
    page?: number,
    searchTerm?: string,
    equipmentsTypeUuid?: string,
    isActive?: boolean
  ): Promise<{
    total: number;
    data: TEquipment[];
  }> {
    const limitVal = limit ?? 10;
    const pageVal = page ?? 1;
    const offset = (pageVal - 1) * limitVal;
    return this.call({
      path: 'admin/equipments',
      params: {
        limit: limitVal,
        offset,
        searchTerm,
        equipmentsTypeUuid,
        isActive
      }
    });
  }

  createModels(data: TModelCreate) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof File) formData.append(key, value);
      else formData.append(key, value?.toString() || '');
    });
    return this.call({
      path: `admin/models`,
      method: 'post',
      body: formData
    });
  }

  createBrands(data: TCreateBrand) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof File) formData.append(key, value);
      else formData.append(key, value?.toString() || '');
    });
    return this.call({ path: `admin/brands`, method: 'post', body: formData });
  }

  createEquipments(data: TCreateEquipment) {
    return this.call({ path: `admin/equipments`, method: 'post', body: data });
  }

  updateBrands(brandsId: string, data: TCreateBrand) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof File) formData.append(key, value);
      else formData.append(key, value?.toString() || '');
    });
    return this.call({
      path: `admin/brands/${brandsId}`,
      method: 'patch',
      body: formData
    });
  }

  getAdminBrandId(uuid: string): Promise<TBrand> {
    return this.call({ path: `admin/brands/${uuid}` });
  }

  updateModels(modelsId: string, data: TModelCreate) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof File) formData.append(key, value);
      else formData.append(key, value?.toString() || '');
    });
    return this.call({
      path: `admin/models/${modelsId}`,
      method: 'patch',
      body: formData
    });
  }

  getAdminModelId(uuid: string): Promise<TModel> {
    return this.call({ path: `admin/models/${uuid}` });
  }

  updateEquipments(equipmentId: string, data: TCreateEquipment) {
    return this.call({
      path: `admin/equipments/${equipmentId}`,
      method: 'patch',
      body: data
    });
  }

  getAdminEquipmentId(uuid: string): Promise<TEquipment> {
    return this.call({ path: `admin/equipments/${uuid}` });
  }
}

export const autoApi = new AutoApi();
